
import React, { useState, useRef } from 'react';
import { PortfolioItem } from '@/hooks/useProfile';

interface GalleryItemProps {
  item: PortfolioItem;
  itemId: string;
  imageUrl: string | null;
  hasMultipleImages: boolean;
  imageCount: number;
  imageLoadStatus?: Record<string, 'loading' | 'success' | 'error'>;
  aspectRatioClass?: string;
  yearText?: string;
  usernameSlug?: string;
  onOpenModal?: (item: PortfolioItem) => void;
}

const GalleryItem: React.FC<GalleryItemProps> = ({
  item,
  itemId,
  imageUrl,
  hasMultipleImages,
  imageCount,
  imageLoadStatus = {},
  aspectRatioClass = 'aspect-video',
  yearText,
  usernameSlug,
  onOpenModal
}) => {
  const [retryAttempts, setRetryAttempts] = useState(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle image load error with retry
  const handleImageError = () => {
    if (retryAttempts < 2) {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      retryTimeoutRef.current = setTimeout(() => {
        setRetryAttempts(prev => prev + 1);
      }, 1000 * (retryAttempts + 1));
    }
  };

  React.useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  const loadStatus = imageLoadStatus[`${itemId}-0`] || 'loading';
  const isLoading = loadStatus === 'loading';
  const hasError = loadStatus === 'error';

  const handleClick = () => {
    if (onOpenModal) {
      onOpenModal(item);
    }
  };

  return (
    <div className="flex flex-col cursor-pointer w-full mb-2 ">
      <div 
        className="flex flex-col overflow-hidden bg-white rounded-lg border shadow-sm dark:border-gray-800 hover:shadow-md transition-all duration-300 hover:-translate-y-1"
      >
        <div 
          className="relative overflow-hidden bg-background rounded-t-lg" 
          onClick={handleClick}
        >
          <div className={`${aspectRatioClass} relative overflow-hidden bg-muted`}>
            {imageUrl && !hasError && (
              <img
                src={`${imageUrl}${retryAttempts > 0 ? `?retry=${retryAttempts}` : ''}`}
                alt={item.title || 'Portfolio Item'}
                className="w-full h-full object-cover"
                onError={handleImageError}
                loading="lazy"
              />
            )}
            {hasError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <svg className="w-12 h-12 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <p className="text-xs text-muted-foreground mt-2">Image unavailable</p>
              </div>
            )}
            {hasMultipleImages && !hasError && (
              <div className="absolute top-2 right-2 z-10 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
                {imageCount} {imageCount === 1 ? 'image' : 'images'}
              </div>
            )}
          </div>
        </div>
        <div className="px-3 py-2 bg-white border-t border-gray-100 rounded-b-lg">
          <div className="text-sm font-medium text-foreground">
            {item.title || 'Untitled Project'}
          </div>
          {yearText && (
            <div className="text-xs text-muted-foreground mt-1">
              {yearText}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GalleryItem;
